import { Link, useNavigate } from "react-router-dom";
import { BookOpen, BrainCircuit, CalendarDays, Flame, Gem, LockKeyhole, PartyPopper, Play, Rocket, Sparkles, Star } from "lucide-react";
import { Avatar, AVATAR_CHARACTERS, AVATAR_COLORS } from "@/components/Avatar";
import { EmptyState, StatusBadge } from "@/components/States";
import { Button } from "@/components/ui/button";

export function StudentHomeContent({ data, editing, onToggleEditing, onSaveAvatar }) {
  const navigate = useNavigate();
  const { student, study_packs: packs, upcoming_exams: exams, plan, totals } = data;
  const ready = packs.filter((pack) => pack.status === "ready" && pack.questions > 0);
  const nextPack = plan.has_data ? ready.find((pack) => pack.id === plan.items[0].study_pack_id) || ready[0] : ready[0];
  const progress = buildProgress(totals);

  return (
    <div className="student-page" data-testid="student-home">
      <HeroSection student={student} totals={totals} nextPack={nextPack} progress={progress} onStart={(id) => navigate(`/s/estudiar/${id}`)} onToggleEditing={onToggleEditing} />
      <AdventureStats totals={totals} accuracy={progress.accuracy} readyCount={ready.length} />
      {editing && <AvatarEditor student={student} onSave={onSaveAvatar} />}
      {plan.has_data && <TodayPlan plan={plan} />}
      <StudyPacks packs={packs} />
      {exams.length > 0 && <UpcomingExams exams={exams} />}
    </div>
  );
}

function buildProgress(totals) {
  const answered = totals.questions_answered || 0;
  return {
    level: Math.max(1, Math.floor(answered / 20) + 1),
    levelProgress: (answered % 20) * 5,
    remaining: 20 - (answered % 20),
    accuracy: Math.round((totals.accuracy || 0) * 100),
  };
}

function HeroSection({ student, totals, nextPack, progress, onStart, onToggleEditing }) {
  return (
    <section className="grid gap-5 lg:grid-cols-[1.55fr_.72fr]">
      <div className="play-card relative min-h-[330px] overflow-hidden bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-500 p-6 pb-32 text-white sm:min-h-[250px] sm:p-8 sm:pr-36">
        <span className="play-doodle -right-10 -top-12 h-48 w-48 border-[28px] border-white/10" />
        <span className="play-doodle bottom-5 left-[48%] h-8 w-20 rotate-12 bg-amber-300/80" />
        <div className="relative z-10 flex max-w-[76%] items-center gap-2 text-xs font-extrabold uppercase tracking-[0.18em] text-violet-100">
          <PartyPopper className="h-5 w-5 text-amber-300" /> ¡Hola, {student.first_name}!
        </div>
        <h1 className="relative z-10 mt-4 max-w-2xl font-display text-4xl font-extrabold leading-[.95] tracking-tight text-white sm:text-5xl">
          Hoy desbloqueamos algo increíble
        </h1>
        <p className="relative z-10 mt-3 max-w-xl text-sm font-semibold text-violet-100 sm:text-base">
          {totals.questions_answered > 0
            ? `Ya superaste ${totals.questions_answered} preguntas con ${progress.accuracy}% de aciertos. ¡Vamos por más!`
            : "Tu primera misión está lista. Empieza pequeño y sorpréndete con todo lo que puedes lograr."}
        </p>
        {nextPack && (
          <Button onClick={() => onStart(nextPack.id)} className="play-button estudia-touch relative z-10 mt-6 rounded-2xl bg-amber-300 px-7 text-base font-extrabold text-slate-900 hover:bg-amber-200" data-testid="start-study-btn">
            <Play className="mr-2 h-5 w-5 fill-current" /> ¡Vamos a jugar!
          </Button>
        )}
        <button onClick={onToggleEditing} className="animate-bounce-soft absolute bottom-6 right-5 z-10 rounded-[2rem] border-4 border-white/80 bg-amber-300 p-2 shadow-[0_8px_0_#d69c11] sm:right-8" data-testid="avatar-edit-toggle" aria-label="Personalizar avatar">
          <Avatar config={student.avatar_config} name={student.first_name} size="lg" />
        </button>
      </div>
      <LevelCard progress={progress} />
    </section>
  );
}

function LevelCard({ progress }) {
  return (
    <aside className="play-card flex min-h-[250px] rotate-[.7deg] flex-col justify-between bg-amber-300 p-6 text-slate-900">
      <div className="flex items-start justify-between gap-4">
        <span className="play-icon h-20 w-20 bg-orange-500"><Rocket className="h-11 w-11 animate-wiggle" /></span>
        <span className="font-display text-5xl font-extrabold leading-none">{String(progress.level).padStart(2, "0")}</span>
      </div>
      <div>
        <p className="font-display text-2xl font-extrabold">Nivel explorador</p>
        <p className="mt-1 text-sm font-bold text-amber-900">{progress.remaining} retos para el siguiente nivel</p>
        <div className="mt-4 h-4 overflow-hidden rounded-full border-2 border-amber-900/20 bg-white/60">
          <div className="h-full rounded-full bg-orange-500 transition-[width] duration-500" style={{ width: `${progress.levelProgress}%` }} />
        </div>
      </div>
    </aside>
  );
}

function AdventureStats({ totals, accuracy, readyCount }) {
  return (
    <section className="grid gap-4 sm:grid-cols-3" aria-label="Resumen de aventura">
      <StatCard icon={BrainCircuit} value={totals.questions_answered || 0} label="Retos superados" tone="bg-cyan-300" iconTone="bg-cyan-600" textTone="text-cyan-950" />
      <StatCard icon={Flame} value={`${accuracy}%`} label="Precisión mágica" tone="-rotate-[.5deg] bg-pink-300" iconTone="bg-pink-600" textTone="text-pink-950" animated />
      <StatCard icon={Gem} value={readyCount} label="Misiones listas" tone="rotate-[.5deg] bg-lime-300" iconTone="bg-emerald-700" textTone="text-lime-950/70" />
    </section>
  );
}

function StatCard({ icon: Icon, value, label, tone, iconTone, textTone, animated = false }) {
  return (
    <div className={`play-card flex items-center gap-4 p-5 ${tone}`}>
      <span className={`play-icon h-16 w-16 ${iconTone}`}><Icon className={`h-9 w-9 ${animated ? "animate-wiggle" : ""}`} /></span>
      <div><p className="font-display text-3xl font-extrabold">{value}</p><p className={`text-sm font-extrabold ${textTone}`}>{label}</p></div>
    </div>
  );
}

function AvatarEditor({ student, onSave }) {
  return (
    <div className="play-card animate-rise border-violet-200 bg-white p-5" data-testid="avatar-editor">
      <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-slate-900"><Star className="h-6 w-6 fill-amber-300 text-amber-500" /> Tu personaje</h2>
      <div className="mt-4 flex flex-wrap gap-3">
        {AVATAR_CHARACTERS.map((character) => (
          <button key={character} onClick={() => onSave({ ...student.avatar_config, character })} className={`rounded-2xl border-2 p-1 transition-colors ${student.avatar_config?.character === character ? "border-blue-500" : "border-transparent"}`} data-testid={`avatar-character-${character}`}>
            <Avatar config={{ ...student.avatar_config, character }} size="md" testId={`avatar-preview-${character}`} />
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {AVATAR_COLORS.map((color) => (
          <button key={color} onClick={() => onSave({ ...student.avatar_config, color })} className={`estudia-touch rounded-xl px-4 text-sm font-semibold capitalize ${student.avatar_config?.color === color ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`avatar-color-${color}`}>
            {color}
          </button>
        ))}
      </div>
      <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-slate-500">Accesorios</h3>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {(student.avatar_accessories || []).map((accessory) => <AccessoryButton key={accessory.id} accessory={accessory} config={student.avatar_config} onSave={onSave} />)}
      </div>
    </div>
  );
}

function AccessoryButton({ accessory, config, onSave }) {
  const selected = config?.accessory === accessory.id;
  const tone = selected
    ? "border-blue-500 bg-blue-50 text-blue-900"
    : accessory.unlocked ? "border-slate-200 bg-white text-slate-700" : "border-slate-100 bg-slate-50 text-slate-400";
  return (
    <button disabled={!accessory.unlocked} onClick={() => onSave({ ...config, accessory: accessory.id })} className={`estudia-touch flex items-center justify-between rounded-xl border-2 px-4 text-left text-sm font-semibold ${tone}`} data-testid={`avatar-accessory-${accessory.id}`}>
      <span><span className="block">{accessory.label}</span><span className="block text-xs font-normal">{accessory.hint}</span><span className="block text-xs font-bold">{accessory.progress_label}</span></span>
      {!accessory.unlocked && <LockKeyhole className="h-4 w-4" aria-label="Bloqueado" />}
    </button>
  );
}

function TodayPlan({ plan }) {
  return (
    <section className="play-card border-violet-200 bg-white p-5 sm:p-6" data-testid="student-plan">
      <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900"><span className="play-icon h-12 w-12 bg-violet-600"><Sparkles className="h-7 w-7" /></span> Tus misiones de hoy</h2>
      <ul className="mt-4 space-y-2">
        {plan.items.map((item) => (
          <li key={item.study_pack_id} className="flex items-center justify-between gap-3 rounded-2xl border-2 border-violet-100 bg-violet-50 px-4 py-3 transition hover:-translate-y-0.5 hover:border-violet-300" data-testid="student-plan-item">
            <span className="min-w-0"><span className="block truncate text-sm font-bold text-slate-800">{item.subject}</span><span className="text-xs text-slate-500">{item.title}</span></span>
            <span className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-extrabold text-violet-700 shadow-sm">{item.minutes} min</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StudyPacks({ packs }) {
  return (
    <section data-testid="student-packs">
      <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900"><span className="play-icon h-12 w-12 bg-cyan-500"><BookOpen className="h-7 w-7" /></span> Elige tu próxima aventura</h2>
      {packs.length === 0 ? (
        <EmptyState title="Todavia no hay material" description="Cuando tu familia suba fotos de tu cuaderno, aparecera aqui listo para practicar." testId="student-packs-empty" />
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">{packs.map((pack) => <StudyPackCard key={pack.id} pack={pack} />)}</div>
      )}
    </section>
  );
}

function StudyPackCard({ pack }) {
  const playable = pack.status === "ready" && pack.questions > 0;
  return (
    <div className="play-card bg-white p-5" data-testid="student-pack-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><h3 className="truncate font-display text-xl font-extrabold text-slate-900">{pack.title}</h3><p className="text-sm text-slate-500">{pack.subject}</p></div>
        <StatusBadge status={pack.status} />
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-sm text-slate-600"><BrainCircuit className="h-5 w-5 text-cyan-500" /> {pack.questions} desafíos</p>
      {playable ? (
        <Link to={`/s/estudiar/${pack.id}`}><Button className="play-button estudia-touch mt-4 w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 font-extrabold hover:from-violet-500 hover:to-fuchsia-500" data-testid={`study-pack-btn-${pack.id}`}><Play className="mr-2 h-4 w-4 fill-current" /> Empezar misión</Button></Link>
      ) : (
        <p className="mt-4 text-xs text-slate-500" data-testid="pack-not-ready-hint">{pack.status === "failed" ? "Este material tuvo un problema. Avisa a tu familia." : "Todavia se esta preparando. Vuelve en un rato."}</p>
      )}
    </div>
  );
}

function UpcomingExams({ exams }) {
  return (
    <section className="play-card bg-cyan-50 p-5" data-testid="student-exams">
      <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold text-slate-900"><span className="play-icon h-12 w-12 bg-cyan-500"><CalendarDays className="h-7 w-7" /></span> Próximos desafíos</h2>
      <ul className="mt-4 space-y-2">
        {exams.map((exam) => (
          <li key={exam.id} className="flex items-center justify-between gap-3 text-sm" data-testid="student-exam-row">
            <span className="min-w-0"><span className="block truncate font-semibold text-slate-800">{exam.title}</span><span className="text-xs text-slate-500">{exam.subject}</span></span>
            <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${exam.days_left <= 3 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{exam.days_left < 0 ? "pasado" : `${exam.days_left} dias`}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
